import { useEffect, useState, useCallback } from "react";
import Graph from "graphology";
import {
  SigmaContainer,
  useLoadGraph,
  useSigma,
  useRegisterEvents,
  useCamera,
  ControlsContainer,
} from "@react-sigma/core";
import circular from "graphology-layout/circular";
import forceAtlas2 from "graphology-layout-forceatlas2";
import { cropToLargestConnectedComponent } from "graphology-components";
// import { drawHover } from "./canvas-utils";

import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import AccountTreeIcon from "@mui/icons-material/AccountTree";

import { useHttpPost } from "./hooks";

import { GraphSearchControl, GraphData } from "./GraphSearchControl";
import { GraphRankControl } from "./GraphRankControl";
import { GraphSearchInput } from "./GraphSearchInput";

import { storyFilterBoxState } from "./JotaiAtoms";
import { makeStoriesRequest } from "./FetchStories";

import {
  projectPickerState,
  timeFilterState,
  negateLabelsState,
  reloadState,
  peopleDataState,
  appBarCustomContentState,
  appBarSearchContentState,
  graphDataState,
  graphSearchSelectionState,
} from "./JotaiAtoms";
import { useAtomValue, useSetAtom } from "jotai";

import * as lodash from "lodash";
import { DarkStoryPreview } from "./StoryDetails";
import log from "./logger";
import { StoryItem } from "./StoryItem";
import "@react-sigma/core/lib/style.css";

function getMouseLayer() {
  return document.querySelector(".sigma-mouse");
}

function LoadGraph({ data, rankby }: { data?: GraphData; rankby?: string }) {
  const loadGraph = useLoadGraph();
  const graph = new Graph();
  const theme = useTheme();
  const sigma = useSigma();

  const reload = useAtomValue(reloadState);
  const { zoomIn, zoomOut, reset, goto, gotoNode } = useCamera();

  useEffect(() => {
    reset();
    // Shift camera left to balance with right preview panel
    setTimeout(() => {
      const camera = sigma.getCamera();
      const currentState = camera.getState();
      goto({
        x: currentState.x + 0.15,
        y: currentState.y,
        ratio: currentState.ratio,
      });
    }, 100);
  }, [reload]);

  useEffect(() => {
    if (!data) {
      return;
    }

    graph.clear();

    const colors = theme.palette.mode === 'dark'
      ? [
          "#9aa0a6",
          "#8ab4f8",
          "#aecbfa",
          "#c58af9",
          "#f6aea9",
          "#f28b82",
        ]
      : [
          "grey",
          "lightsteelblue",
          "cornflowerblue",
          "thistle",
          "lightpink",
          "indianred",
        ];

    for (let [k, v] of Object.entries(data.nodes)) {
      const weight = v[0];
      const colorIdx = lodash.clamp(
        Math.round(weight * (colors.length - 1)),
        0,
        colors.length - 1
      );
      graph.addNode(k, {
        size: weight * 30 + 1,
        label: v[1] ? v[1] : k,
        color: colors[colorIdx],
      });
    }

    for (let e of data.edges) {
      graph.addEdge(e[0], e[1]);
    }

    cropToLargestConnectedComponent(graph);
    circular.assign(graph);
    const settings = forceAtlas2.inferSettings(graph);
    forceAtlas2.assign(graph, { settings, iterations: 600 });

    loadGraph(graph);
  }, [data]);

  return null;
}

type GraphEventsControllerProps = {
  setHoveredNode: (node: string) => void;
  onNodeClick?: (node: any) => void;
  onNodeHover?: (node: any) => void;
  children?: any;
};
export function GraphEventsController({
  setHoveredNode,
  onNodeClick,
  onNodeHover,
  children,
}: GraphEventsControllerProps) {
  const sigma = useSigma();
  const graph = sigma.getGraph();
  const registerEvents = useRegisterEvents();

  useEffect(() => {
    registerEvents({
      clickNode({ node }) {
        if (!graph.getNodeAttribute(node, "hidden")) {
          if (onNodeClick) {
            const attrs = graph.getNodeAttributes(node);
            onNodeClick(attrs);
          }
        }
      },
      enterNode({ node }) {
        setHoveredNode(node);
        // TODO: Find a better way to get the DOM mouse layer:
        const mouseLayer = getMouseLayer();
        if (mouseLayer) mouseLayer.classList.add("mouse-pointer");

        if (onNodeHover) {
          const attrs = graph.getNodeAttributes(node);
          onNodeHover(attrs);
        }
      },
      leaveNode() {
        setHoveredNode("");
        // TODO: Find a better way to get the DOM mouse layer:
        const mouseLayer = getMouseLayer();
        if (mouseLayer) mouseLayer.classList.remove("mouse-pointer");
        if (onNodeHover) {
          onNodeHover(undefined);
        }
      },
    });
  }, []);

  return <>{children}</>;
}

function useDebounce<Type>(value: Type, delay: number) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (value !== debouncedValue) setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

type GraphSettingsControllerProps = {
  hoveredNode: string;
  children?: any;
};

export function GraphSettingsController({
  hoveredNode,
  children,
}: GraphSettingsControllerProps) {
  const sigma = useSigma();
  const graph = sigma.getGraph();
  const theme = useTheme();

  const debouncedHoveredNode = useDebounce(hoveredNode, 40);

  const NODE_FADE_COLOR = theme.palette.mode === 'dark' ? "#5f6368" : "#bbb";
  const EDGE_FADE_COLOR = theme.palette.mode === 'dark' ? "#3c4043" : "#eee";
  const HIGHLIGHTED_NODE_COLOR = theme.palette.mode === 'dark' ? '#fbbf24' : '#f59e0b'; // Bright orange/yellow - good contrast with both white and dark text

  useEffect(() => {
    // const hoveredColor = debouncedHoveredNode ? sigma.getNodeDisplayData(debouncedHoveredNode)!.color : "";
    const hoveredColor = debouncedHoveredNode ? "red" : "";

    sigma.setSetting(
      "nodeReducer",
      debouncedHoveredNode
        ? (node, data) => {
            const isConnected = node === debouncedHoveredNode ||
              graph.hasEdge(node, debouncedHoveredNode) ||
              graph.hasEdge(debouncedHoveredNode, node);

            return isConnected
              ? {
                  ...data,
                  zIndex: 1,
                  highlighted: false, // Don't use white halo
                  hovered: node === debouncedHoveredNode,
                  // Make the hovered node brighter instead of using white halo
                  color: node === debouncedHoveredNode ? HIGHLIGHTED_NODE_COLOR : data.color,
                  size: node === debouncedHoveredNode ? data.size * 1.5 : data.size,
                }
              : {
                  ...data,
                  zIndex: 0,
                  label: "",
                  color: NODE_FADE_COLOR,
                  image: null,
                  highlighted: false,
                };
          }
        : (node, data) => {
            // When not hovering, check if node is highlighted by search
            try {
              const isHighlighted = graph.getNodeAttribute(node, 'highlighted');
              if (isHighlighted) {
                return {
                  ...data,
                  highlighted: false, // Don't use white halo
                  color: HIGHLIGHTED_NODE_COLOR,
                  size: data.size * 1.5,
                };
              }
            } catch (e) {}
            return data;
          }
    );
    sigma.setSetting(
      "edgeReducer",
      debouncedHoveredNode
        ? (edge, data) =>
            graph.hasExtremity(edge, debouncedHoveredNode)
              ? { ...data, color: hoveredColor, size: 4 }
              : { ...data, color: EDGE_FADE_COLOR, hidden: true }
        : null
    );
  }, [debouncedHoveredNode, sigma, graph, NODE_FADE_COLOR, EDGE_FADE_COLOR, HIGHLIGHTED_NODE_COLOR]);

  return <>{children}</>;
}

type GraphContainerProps = {
  data?: GraphData;
  onStoryClicked: (storyId: string) => void;
  rankby?: string;
  onHover: (storyId: string) => void;
  children: any;
  isLoading: boolean;
};

function GraphContainer({
  data,
  onStoryClicked,
  rankby,
  onHover,
  children,
  isLoading,
}: GraphContainerProps) {
  const [hoveredNode, setHoveredNode] = useState("");
  const theme = useTheme();

  const handleNodeClick = (node: any) => {
    if (node && node.label) {
      onStoryClicked(node.label);
    }
  };
  const handleNodeHover = (node: any) => {
    if (node && node.label) {
      onHover(node.label);
    }
  };

  const isEmpty = !data || !data.nodes || Object.keys(data.nodes).length === 0;

  if (!isLoading && isEmpty) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
        }}
      >
        <AccountTreeIcon
          sx={{
            fontSize: "64px",
            color: "var(--text-secondary)",
            opacity: 0.3,
            mb: 1,
          }}
        />
        <Box
          sx={{
            color: "var(--text-primary)",
            fontSize: "16px",
            fontWeight: 500,
          }}
        >
          No graph data available
        </Box>
        <Box
          sx={{
            color: "var(--text-secondary)",
            fontSize: "13px",
            maxWidth: "300px",
            textAlign: "center",
          }}
        >
          Try adjusting your filters or time range to see connections between stories
        </Box>
      </Box>
    );
  }

  return (
    <SigmaContainer
      settings={{
        allowInvalidContainer: true,
        renderEdgeLabels: false,
        defaultNodeColor: theme.palette.mode === 'dark' ? '#9aa0a6' : 'grey',
        defaultEdgeColor: theme.palette.mode === 'dark' ? '#5f6368' : '#ccc',
        // Deeper, muted blue - professional and not acid
        labelColor: { color: theme.palette.mode === 'dark' ? '#6b9bd5' : '#202124' },
        renderLabels: true,
        labelDensity: 1,
        labelGridCellSize: 60,
        labelRenderedSizeThreshold: 0,
      }}
      style={{
        backgroundColor: theme.palette.mode === 'dark' ? '#121212' : '#ffffff',
      }}
    >
      <GraphSettingsController hoveredNode={hoveredNode} />
      <GraphEventsController
        setHoveredNode={setHoveredNode}
        onNodeClick={handleNodeClick}
        onNodeHover={handleNodeHover}
      />
      <LoadGraph data={data} rankby={rankby} />
      <GraphSearchControl data={data} />
      {children}
    </SigmaContainer>
  );
}

type StoryGraphProps = {
  onStoryClicked: (storyId: string) => void;
  rankby?: string;
  onHover: (storyId: string) => void;
  children: any;
};

function StoryGraph({
  onStoryClicked,
  rankby,
  onHover,
  children,
}: StoryGraphProps) {
  const [data, setData] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const project = useAtomValue(projectPickerState);
  const timeFilter = useAtomValue(timeFilterState);
  const filters = useAtomValue(storyFilterBoxState);
  const negateLabels = useAtomValue(negateLabelsState);
  const httpPost = useHttpPost();
  const people = useAtomValue(peopleDataState);
  const setGraphData = useSetAtom(graphDataState);

  const getData = async (
    project: string,
    filters: string[],
    timeFilter: string,
    rankby?: string,
    negateLabels?: boolean
  ) => {
    setIsLoading(true);
    let labelFilters: string[] | undefined = undefined;
    let userFilters: string[] | undefined = undefined;
    if (filters.length > 0) {
      for (const f of filters) {
        if (f.startsWith("@")) {
          if (!userFilters) {
            userFilters = [f];
          } else {
            userFilters.push(f);
          }
        } else {
          if (!labelFilters) {
            labelFilters = [f];
          } else {
            labelFilters.push(f);
          }
        }
      }
    }

    const r = makeStoriesRequest(people, userFilters);

    try {
      const response = await httpPost("stories", {
        edges: true,
        period: timeFilter,
        labels: labelFilters,
        rankby,
        project: project,
        negate: negateLabels,
        accounts: r?.accounts,
      });
      setData(response.data);
      setGraphData(response.data);
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (project && filters && rankby) {
      const projFilters = project in filters ? filters[project] : [];
      getData(project, projFilters, timeFilter, rankby, negateLabels);
    } else if (!project || !filters) {
      // Only set loading to false if we're missing project/filters (won't fetch)
      // Don't set to false if we're just waiting for rankby (will fetch soon)
      setIsLoading(false);
    }
  }, [project, timeFilter, filters, rankby, negateLabels]);

  return (
    <GraphContainer
      data={data}
      onStoryClicked={onStoryClicked}
      rankby={rankby}
      onHover={onHover}
      isLoading={isLoading}
    >
      {children}
    </GraphContainer>
  );
}

function RightPane({ hoverStory }: { hoverStory?: string }) {
  const [preview, setPreview] = useState<StoryItem>();
  const httpPost = useHttpPost();

  useEffect(() => {
    const getPreview = async () => {
      if (!hoverStory) {
        return;
      }

      try {
        const response = await httpPost("stories", {
          stories: true,
          story: hoverStory,
        });
        setPreview(response.data.stories[0]);
      } catch (err) {
        log.debug(err);
      }
    };
    if (hoverStory) {
      getPreview();
    }
    {
      setPreview(undefined);
    }
  }, [hoverStory]);

  return (
    <Stack direction={"column"} sx={{ maxWidth: 300 }}>
      {preview && (
        <DarkStoryPreview item={preview} fontSize={11} key={preview.SK} />
      )}
    </Stack>
  );
}

function GraphView({
  onStoryClicked,
}: {
  onStoryClicked: (storyId: string) => void;
}) {
  const [rankby, setRankby] = useState<string>();
  const [hoverStory, setHoverStory] = useState<string>();
  const setAppBarContent = useSetAtom(appBarCustomContentState);
  const setAppBarSearch = useSetAtom(appBarSearchContentState);
  const setGraphData = useSetAtom(graphDataState);
  const setGraphSearch = useSetAtom(graphSearchSelectionState);

  const handleChange = useCallback((rankby: string) => {
    setRankby(rankby);
  }, []);

  const handleHover = (storyId: string) => {
    setHoverStory(storyId);
  };

  // Set AppBar content on mount, clear on unmount
  useEffect(() => {
    setAppBarSearch(<GraphSearchInput />);
    setAppBarContent(<GraphRankControl onRank={handleChange} />);
    return () => {
      setAppBarSearch(null);
      setAppBarContent(null);
      setGraphData(undefined);
      setGraphSearch("");
    };
  }, [handleChange, setAppBarContent, setAppBarSearch, setGraphData, setGraphSearch]);

  return (
    <Stack direction={"row"} sx={{ width: "100%", height: "100%" }}>
      <Box sx={{ width: "100%", position: "relative" }}>
        <StoryGraph
          onStoryClicked={onStoryClicked}
          rankby={rankby}
          onHover={handleHover}
        >
          <ControlsContainer
            position={"top-right"}
            style={{
              border: 0,
              backgroundColor: "transparent",
              height: "100%",
              pointerEvents: "none",
            }}
          >
            {/* Story preview on the right side */}
            <Box sx={{ overflowY: "auto", pointerEvents: "auto" }}>
              <RightPane hoverStory={hoverStory} />
            </Box>
          </ControlsContainer>
        </StoryGraph>
      </Box>
    </Stack>
  );
}

export default GraphView;
